/**
 * SABI — Header
 * Handles: desktop mega-menu open/close, mobile drawer with accordion
 * submenus, the search flyout, and — when "Stick to top after scrolling"
 * is on — pinning the header to position:fixed once the page has scrolled
 * up to meet it (see sabi-header.css for why plain CSS position:sticky
 * doesn't work inside this theme's CSS Grid body), plus (for a transparent
 * sticky header) swapping to the solid colors once scrolled past the hero
 * so the header doesn't stay invisible white-on-white.
 */
(function () {
  if (customElements.get('sabi-header')) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const CLOSE_DELAY = 150;

  class SabiHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.sabi-header__bar');
      this.navItems = Array.from(this.querySelectorAll('[data-sabi-nav-item]'));
      this.backdrop = this.querySelector('[data-sabi-backdrop]');
      this.drawer = this.querySelector('[data-sabi-drawer]');
      this.searchPanel = this.querySelector('[data-sabi-search-panel]');
      this.closeTimer = null;

      this._bindMegaMenu();
      this._bindDrawer();
      this._bindSearch();
      this._bindGlobalDismiss();
      this._bindStickyScroll();
    }

    _bindMegaMenu() {
      this.navItems.forEach((item) => {
        if (!item.classList.contains('has-dropdown')) return;
        const trigger = item.querySelector('[data-sabi-nav-trigger]');
        const panel = item.querySelector('[data-sabi-mega]');
        if (!trigger || !panel) return;

        const open = () => {
          if (!window.matchMedia(DESKTOP_QUERY).matches) return;
          clearTimeout(this.closeTimer);
          this.navItems.forEach((other) => {
            if (other !== item) this._closeItem(other);
          });
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        };

        const scheduleClose = () => {
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => this._closeItem(item), CLOSE_DELAY);
        };

        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', scheduleClose);
        item.addEventListener('focusin', open);
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) scheduleClose();
        });

        trigger.addEventListener('click', (event) => {
          if (window.matchMedia(DESKTOP_QUERY).matches) return;
          event.preventDefault();
          const isOpen = item.classList.contains('is-open');
          this.navItems.forEach((other) => this._closeItem(other));
          if (!isOpen) {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.navItems.forEach((item) => this._closeItem(item));
      });
    }

    _closeItem(item) {
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-sabi-nav-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    _bindDrawer() {
      const openBtn = this.querySelector('[data-sabi-drawer-open]');
      const closeBtn = this.querySelector('[data-sabi-drawer-close]');
      if (!openBtn || !this.drawer) return;

      const open = () => {
        this.drawer.hidden = false;
        this._showBackdrop();
        requestAnimationFrame(() => this.drawer.classList.add('is-open'));
        openBtn.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('sabi-scroll-lock');
        const firstLink = this.drawer.querySelector('a, button');
        if (firstLink) firstLink.focus({ preventScroll: true });
      };

      const close = () => {
        this.drawer.classList.remove('is-open');
        this._hideBackdrop();
        openBtn.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('sabi-scroll-lock');
        setTimeout(() => {
          if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
        }, 240);
        openBtn.focus({ preventScroll: true });
      };

      openBtn.addEventListener('click', open);
      if (closeBtn) closeBtn.addEventListener('click', close);
      this._closeDrawer = close;

      this.drawer.querySelectorAll('.sabi-header__drawer-toggle').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const row = toggle.closest('.sabi-header__drawer-row');
          const collapse = row && row.nextElementSibling;
          if (!collapse) return;
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          collapse.classList.toggle('is-open', !expanded);
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) close();
      });
    }

    _bindSearch() {
      const openBtns = this.querySelectorAll('[data-sabi-search-open]');
      const closeBtn = this.querySelector('[data-sabi-search-close]');
      if (!openBtns.length || !this.searchPanel) return;

      const open = () => {
        this.searchPanel.hidden = false;
        this._showBackdrop();
        const input = this.searchPanel.querySelector('[data-sabi-search-input]');
        if (input) input.focus({ preventScroll: true });
      };

      const close = () => {
        this.searchPanel.hidden = true;
        this._hideBackdrop();
      };

      openBtns.forEach((btn) => btn.addEventListener('click', open));
      if (closeBtn) closeBtn.addEventListener('click', close);
      this._closeSearch = close;

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.searchPanel.hidden) close();
      });
    }

    _showBackdrop() {
      if (!this.backdrop) return;
      this.backdrop.hidden = false;
      requestAnimationFrame(() => this.backdrop.classList.add('is-visible'));
      this.backdrop.addEventListener('click', this._onBackdropClick.bind(this), { once: true });
    }

    _hideBackdrop() {
      if (!this.backdrop) return;
      this.backdrop.classList.remove('is-visible');
      setTimeout(() => {
        if (!this.backdrop.classList.contains('is-visible')) this.backdrop.hidden = true;
      }, 200);
    }

    _onBackdropClick() {
      if (this.drawer && this.drawer.classList.contains('is-open') && this._closeDrawer) this._closeDrawer();
      if (this.searchPanel && !this.searchPanel.hidden && this._closeSearch) this._closeSearch();
    }

    _bindGlobalDismiss() {
      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.navItems.forEach((item) => this._closeItem(item));
      });
    }

    _bindStickyScroll() {
      const isTransparent = this.classList.contains('sabi-header--transparent');
      const isSticky = this.classList.contains('sabi-header--sticky');
      if (!isSticky) return;

      let pinThreshold = 0;
      let solidThreshold = 0;
      let ticking = false;

      const measure = () => {
        // Measure the header's true in-flow position (i.e. the height of
        // whatever sits above it, like the announcement bar) so we know
        // exactly when it has scrolled up to the top of the viewport. If
        // it's currently pinned (position: fixed), drop that state first
        // so the measurement reflects its natural, un-pinned position.
        const wasPinned = this.classList.contains('is-pinned');
        if (wasPinned) this.classList.remove('is-pinned');
        pinThreshold = this.getBoundingClientRect().top + window.scrollY;
        if (wasPinned) this.classList.add('is-pinned');
        solidThreshold = pinThreshold + this.offsetHeight;
      };

      const update = () => {
        const y = window.scrollY;
        this.classList.toggle('is-pinned', y >= pinThreshold);
        if (isTransparent) {
          this.classList.toggle('is-scrolled', y >= solidThreshold);
        }
        ticking = false;
      };

      measure();
      update();

      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      }, { passive: true });

      window.addEventListener('resize', () => {
        measure();
        update();
      });
    }
  }

  customElements.define('sabi-header', SabiHeader);
})();
